import type { TradeOfferItem } from '@prisma/client'
import type { Player } from '@/lib/types/roster'
import { TRADE_TYPES } from '@/lib/constants/trade-types'

// Add future money to the Player type
type PlayerWithFutureMoney = Player & {
    futureMoney?: Array<{
        year: number
        value: 'all' | 'custom'
        customValue?: number
    }>
}

interface DraftPickItem {
    year: number
    round: number
    pick?: number | null
    overall?: number | null
    comment?: string
}

interface TradeItemsResult {
    players: PlayerWithFutureMoney[]
    picks: DraftPickItem[]
}

// Extend the TradeOfferItem type to include the player property from the API response
interface TradeOfferItemWithPlayer extends TradeOfferItem {
    player?: {
        id: number
        firstName: string | null
        lastName: string | null
        mlbPlayerId: number | null
        contractYears: Array<{
            year: number
            salary: number
            yrOption: string | null
        }>
        mlbTeam?: {
            id: number
            name: string
            teamAbbrev: string
        }
    }
}

export function transformTradeOfferItems(items: TradeOfferItemWithPlayer[], teamId: number): TradeItemsResult {
    const players: PlayerWithFutureMoney[] = []
    const picks: DraftPickItem[] = []
    const futureMoneyByPlayerId: Record<number, Array<{ year: number; value: 'all' | 'custom'; customValue?: number }>> = {}

    // First pass: collect all future money items
    items.forEach(item => {
        if (item.team2Id === teamId && item.tradeType === TRADE_TYPES.FUTURE_MONEY && item.playerId && item.year) {
            if (!futureMoneyByPlayerId[item.playerId]) {
                futureMoneyByPlayerId[item.playerId] = []
            }
            futureMoneyByPlayerId[item.playerId].push({
                year: item.year,
                value: item.value === 'custom' ? 'custom' : 'all',
                customValue: item.customValue || undefined
            })
        }
    })

    // Sort future money by year for each player
    Object.values(futureMoneyByPlayerId).forEach(moneyArray => {
        moneyArray.sort((a, b) => a.year - b.year)
    })

    // Second pass: process players and picks
    items.forEach(item => {
        if (item.team2Id === teamId) {
            if (item.tradeType === TRADE_TYPES.PLAYER && item.playerId && item.player) {
                const playerData = item.player
                const player: PlayerWithFutureMoney = {
                    id: String(playerData.id),
                    name: `${playerData.firstName || ''} ${playerData.lastName || ''}`.trim(),
                    firstName: playerData.firstName,
                    lastName: playerData.lastName,
                    mlbPlayerId: playerData.mlbPlayerId || 0,
                    type: null,
                    contractYears: playerData.contractYears || [],
                    positionCounts: {},
                    mlbTeam: playerData.mlbTeam ? {
                        id: playerData.mlbTeam.id,
                        name: playerData.mlbTeam.name,
                        abbreviation: playerData.mlbTeam.teamAbbrev,
                        division: ''
                    } : {
                        id: 0,
                        name: '',
                        abbreviation: '',
                        division: ''
                    },
                    playerStatus: undefined
                }

                // Add future money if it exists for this player
                if (futureMoneyByPlayerId[playerData.id]) {
                    player.futureMoney = futureMoneyByPlayerId[playerData.id]
                }

                players.push(player)
            } else if (item.tradeType === TRADE_TYPES.DRAFT_PICK && item.year && item.round) {
                picks.push({
                    year: item.year,
                    round: item.round,
                    pick: item.pick,
                    overall: item.overall,
                    comment: item.comment || undefined
                })
            }
        }
    })

    return { players, picks }
}

export function getTradeOfferStatus(status: string | null): {
    label: string
    color: 'default' | 'primary' | 'success' | 'destructive'
} {
    switch (status?.toLowerCase()) {
        case 'pending':
            return { label: 'Pending', color: 'default' }
        case 'accepted':
            return { label: 'Accepted', color: 'success' }
        case 'rejected':
            return { label: 'Rejected', color: 'destructive' }
        case 'expired':
            return { label: 'Expired', color: 'destructive' }
        case 'countered':
            return { label: 'Countered', color: 'primary' }
        default:
            return { label: 'Unknown', color: 'default' }
    }
} 